import { type PropsWithChildren, forwardRef } from "react";

import type { TabProps } from "./Tab.types";
import { cn } from "$/lib/utils/functions/misc.functions";

const BasicTabStyles =
  "inline-flex border-b-[1px] items-center gap-x-2 whitespace-nowrap  border-tertiary-300 px-3 py-3 text-tertiary-700 hover:opacity-80 focus:outline-none disabled:pointer-events-none disabled:opacity-50";
const BorderedTabStyles =
  "py-1.5 px-4 inline-flex items-center gap-x-2 bg-transparent focus:outline-none  font-semibold rounded disabled:opacity-50 disabled:pointer-events-none";

const Tab = forwardRef<HTMLButtonElement, PropsWithChildren<TabProps>>(
  (
    {
      tabObject,
      tabType,
      isActive,
      onTabChange,
      tabClassName,
      theme,
      ...tabProps
    },
    forwardedRef,
  ) => {
    const { id, label } = tabObject;

    return (
      <button
        ref={forwardedRef}
        id={id}
        type="button"
        className={cn(
          tabType === "basic" ? BasicTabStyles : BorderedTabStyles,
          {
            "focus:text-secondary": tabType === "basic" && theme === "blue",
            "focus:text-forest": tabType === "basic" && theme === "green",
            "focus:text-warning": tabType === "basic" && theme === "orange",

            "text-primary hover:text-secondary hover:text-secondary-600":
              tabType === "bordered" && theme === "blue",
            "text-forest hover:text-forest-600":
              tabType === "bordered" && theme === "green",
            "text-warning hover:text-warning-600":
              tabType === "bordered" && theme === "orange",

            "border-b-[3px] border-secondary font-semibold text-tertiary-800 focus:border-secondary":
              isActive && tabType === "basic" && theme === "blue",
            "border-b-[3px] border-forest font-semibold text-forest focus:border-forest":
              isActive && tabType === "basic" && theme === "green",
            "border-b-[3px] border-warning font-semibold text-warning focus:border-warning":
              isActive && tabType === "basic" && theme === "orange",

            "bg-primary text-white hover:text-white":
              isActive && tabType === "bordered" && theme === "blue",
            "bg-forest text-white hover:text-white":
              isActive && tabType === "bordered" && theme === "green",
            "bg-warning text-white hover:text-white":
              isActive && tabType === "bordered" && theme === "orange",
          },
          tabClassName,
          { "!border-tertiary-300": !isActive },
        )}
        onClick={() => onTabChange(id)}
        role="tab"
        {...tabProps}
      >
        <span>{label}</span>
      </button>
    );
  },
);

Tab.displayName = "Tab";

export default Tab;
